import { useState } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_clearout-pro/artifacts/px7wjijn_Junk%20Removal%20Logo.webp";

export default function LiveChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      text: "Hi there! 👋 I'm here to help you with junk removal questions. How can I assist you today?",
    },
  ]);
  const [input, setInput] = useState('');

  const quickReplies = [
    "Get a free quote",
    "What do you remove?",
    "Service areas",
    "Talk to a human",
  ];

  const handleQuickReply = (reply) => {
    setMessages([...messages, { id: Date.now(), type: 'user', text: reply }]);
    
    setTimeout(() => {
      let response = '';
      if (reply.includes('quote')) {
        response = "Great! For the fastest quote, text photos of your junk to (918) 321-0654 with your name and address. Or fill out our contact form and we'll get back to you ASAP!";
      } else if (reply.includes('remove')) {
        response = "We remove almost everything! Furniture, appliances, mattresses, yard waste, construction debris, hot tubs, decks, and more. We don't accept chemicals, large amounts of concrete, or hazardous materials.";
      } else if (reply.includes('area')) {
        response = "We serve most of Northeast Oklahoma including Cherokee, Adair, Mayes, Delaware, Craig, Ottawa, Wagoner, Muskogee, and Sequoyah counties. Not sure? Give us a call!";
      } else if (reply.includes('human')) {
        response = "No problem! Call us at (918) 321-0654 or email support@junkremovalmd.com. We're available 8 AM - 8 PM daily.";
      }
      setMessages(prev => [...prev, { id: Date.now(), type: 'bot', text: response }]);
    }, 500);
  };

  const handleSend = () => {
    if (!input.trim()) return;
    
    setMessages([...messages, { id: Date.now(), type: 'user', text: input }]);
    setInput('');
    
    setTimeout(() => {
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'bot',
        text: "Thanks for your message! For the fastest response, please call us at (918) 321-0654 or text photos of your junk for a quick quote. We're here to help!",
      }]);
    }, 500);
  };

  return (
    <>
      {/* Chat Bubble Button - Red for visibility */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 left-6 z-40 flex items-center gap-2 bg-[#dc2626] hover:bg-[#b91c1c] text-white p-4 rounded-full shadow-lg shadow-red-900/30 chat-bubble btn-press transition-all"
          aria-label="Open chat"
          data-testid="chat-open-btn"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className="fixed bottom-6 left-6 z-50 flex flex-col w-[calc(100vw-48px)] sm:w-96 h-[70vh] sm:h-[500px] bg-white rounded-2xl shadow-2xl overflow-hidden"
          data-testid="chat-window"
        >
          {/* Header */}
          <div className="bg-[#1a1a1a] text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Chat" className="w-10 h-10 rounded-full bg-[#1a1a1a] object-contain p-1" />
              <div>
                <h4 className="font-bold text-sm">Junk Removal M.D.</h4>
                <span className="text-xs text-gray-400">Usually replies instantly</span>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-white/10 rounded"
              aria-label="Close chat"
              data-testid="chat-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#f5f5f5]">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-2 rounded-2xl text-sm ${
                    msg.type === 'user'
                      ? 'bg-[#dc2626] text-white rounded-br-md'
                      : 'bg-white text-[#1a1a1a] rounded-bl-md shadow-sm'
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}
          </div>

          {/* Quick Replies */}
          <div className="px-4 py-2 bg-white border-t flex flex-wrap gap-2">
            {quickReplies.map((reply) => (
              <button
                key={reply}
                onClick={() => handleQuickReply(reply)}
                className="text-xs px-3 py-1.5 bg-[#f5f5f5] hover:bg-[#dc2626] hover:text-white rounded-full transition-colors"
                data-testid={`quick-reply-${reply.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {reply}
              </button>
            ))}
          </div>

          {/* Input */}
          <div className="p-4 bg-white border-t flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Type a message..."
              className="flex-1 px-4 py-2 border rounded-full text-sm focus:outline-none focus:border-[#dc2626]"
              data-testid="chat-input"
            />
            <Button
              onClick={handleSend}
              size="icon"
              className="bg-[#dc2626] hover:bg-[#b91c1c] rounded-full"
              data-testid="chat-send-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
